import { buildSiColumn } from "./SiTableCell";

// Date formatting function from task module
const convertDateString = (dateString) => {
  if (!dateString) return "";
  const dateObject = new Date(dateString);
  const formattedDate = `${dateObject.getDate()}-${dateObject.toLocaleString(
    "en-US",
    { month: "short" }
  )}-${dateObject.getFullYear()}`;
  return <p>{formattedDate}</p>;
};

// Helper function to calculate progress based on MRQ, PO, MRV status
export const calculateProgress = (material) => {
  const hasMrq = material.mrqMaterials?.some((m) => m?.mrq?.id);
  const hasPo = material.mrqMaterials?.some(
    (m) => m?.poId || m?.poMaterials?.[0]?.po?.id
  );
  const hasMrv =
    material.mrvMaterials?.[0]?.id ||
    material.mrqMaterials?.some((m) => m?.mrvMrqMaterials?.[0]?.mrv?.id);

  if (hasMrv) return 100;
  if (hasPo) return 60;
  if (hasMrq) return 30;
  return 0;
};

// Ensure value is a number; returns 0 if not a valid number
const toNumber = (value) => {
  if (value == null || value === "") return 0;
  const n = Number(value);
  return typeof n === "number" && !Number.isNaN(n) ? n : 0;
};

// Helper function to get total cost from PO (unit cost × consolidated quantity)
// Uses the first available unit cost from poMaterials and multiplies by consolidated material quantity
// Ensures both unit cost and quantity are numbers before multiplying
export const getTotalCost = (material) => {
  const consolidatedQuantity = toNumber(material.quantity);
  if (consolidatedQuantity <= 0) return null;

  // Find first unit cost from poMaterials
  let unitCost = 0;
  for (const mm of material.mrqMaterials || []) {
    for (const pm of mm.poMaterials || []) {
      const cost = toNumber(pm.cost);
      if (cost > 0) {
        unitCost = cost;
        break;
      }
    }
    if (unitCost > 0) break;
  }

  // Fallback: check mrqMaterial cost if no poMaterials
  if (unitCost === 0) {
    const fallbackCost = material.mrqMaterials?.[0]?.cost;
    unitCost = toNumber(fallbackCost);
  }

  if (unitCost > 0) {
    return unitCost * consolidatedQuantity;
  }

  return null;
};

export const buildSchedule4DColumns = ({ onSiChange, siUpdatingIds } = {}) => [
  // {
  //   accessorKey: "drawingTitle",
  //   header: "Drawing Title",
  //   enableEditing: false,
  //   size: 150,
  //   Cell: ({ row }) => {
  //     return row.original.drawing?.title || "-";
  //   },
  // },
  // {
  //   accessorKey: "drawingNumber",
  //   header: "Drawing #",
  //   enableEditing: false,
  //   size: 120,
  //   Cell: ({ row }) => {
  //     return row.original.drawing?.dwgNumber || "-";
  //   },
  // },
  // {
  //   accessorKey: "revision",
  //   header: "Revision",
  //   enableEditing: false,
  //   size: 80,
  //   Cell: ({ row }) => {
  //     return row.original.drawing?.revision || "-";
  //   },
  // },
  // {
  //   accessorKey: "tagNumber",
  //   header: "Tag #",
  //   enableEditing: false,
  //   size: 100,
  //   Cell: ({ row }) => {
  //     const m = row.original;
  //     return m?.tagNumber || m?.taggedMaterial?.tagNumber || m?.item || "-";
  //   },
  // },
  {
    accessorKey: "taskCode",
    header: "WBS ID",
    enableEditing: false,
    size: 100,
    Cell: ({ row }) => {
      const m = row.original;
      return m.taskCode || m?.tagNumber || m?.taggedMaterial?.tagNumber || m?.item || "-";
    },
  },
  {
    header: "Task",
    accessorKey: "task",
    enableEditing: false,
    size: 250,
    Cell: ({ row }) => {
      const m = row.original;
      return m.task 
    },
  },
  {
    accessorKey: "baselineStart",
    header: `Baseline Start`,
    size: 120,
    enableColumnFilter: false,
    enableEditing: false,
    Cell: ({ cell }) => {
      const dateValue = cell.getValue();
      return dateValue ? (
        <div style={{ whiteSpace: "wrap" }}>
          {convertDateString(dateValue)}
        </div>
      ) : (
        ""
      );
    },
    muiEditTextFieldProps: () => {
      return {
        type: "date",
      };
    },
  },
  {
    accessorKey: "baselineEnd",
    header: `Baseline End`,
    size: 120,
    enableColumnFilter: false,
    enableEditing: false,
    Cell: ({ cell }) => {
      const dateValue = cell.getValue();
      return dateValue ? (
        <div style={{ whiteSpace: "wrap" }}>
          {convertDateString(dateValue)}
        </div>
      ) : (
        ""
      );
    },
    muiEditTextFieldProps: () => {
      return {
        type: "date",
      };
    },
  },
  {
    accessorKey: "revisedStart",
    header: `Revised Start`,
    size: 120,
    enableColumnFilter: false,
    enableEditing: false,
    Cell: ({ cell }) => {
      const dateValue = cell.getValue();
      return dateValue ? (
        <div style={{ whiteSpace: "wrap" }}>
          {convertDateString(dateValue)}
        </div>
      ) : (
        ""
      );
    },
    muiEditTextFieldProps: () => {
      return {
        type: "date",
      };
    },
  },
  {
    accessorKey: "revisedEnd",
    header: `Revised End`,
    size: 120,
    enableColumnFilter: false,
    enableEditing: false,
    Cell: ({ cell }) => {
      const dateValue = cell.getValue();
      return dateValue ? (
        <div style={{ whiteSpace: "wrap" }}>
          {convertDateString(dateValue)}
        </div>
      ) : (
        ""
      );
    },
    muiEditTextFieldProps: () => {
      return {
        type: "date",
      };
    },
  },

  // {
  //   header: "Quantity",
  //   accessorKey: "quantity",
  //   enableEditing: false,
  //   size: 100,
  //   muiTableHeadCellProps: {
  //     align: "center",
  //   },
  //   muiTableBodyCellProps: {
  //     align: "center",
  //   },
  //   Cell: ({ row }) => {
  //     // Quantity comes from MRQ procuredQty (summed from all mrqMaterials for consolidated materials)
  //     // Falls back to material.quantity if no MRQ exists
  //     // See api.js consolidation logic: sum of procuredQty from all mrqMaterials
  //     const qty = row.original?.quantity;
  //     function formatNumber(num) {
  //       if (num === null || num === undefined || num === 0) return "-";
  //       return Number.isInteger(num) ? num : num.toFixed(2);
  //     }
  //     return formatNumber(Number(qty));
  //   },
  // },
  // {
  //   header: "Unit",
  //   accessorKey: "unit",
  //   enableEditing: false,
  //   size: 80,
  //   muiTableHeadCellProps: {
  //     align: "center",
  //   },
  //   muiTableBodyCellProps: {
  //     align: "center",
  //   },
  //   Cell: ({ row }) => {
  //     return row.original?.unit || "Nos";
  //   },
  // },
  {
    header: "MRQ",
    accessorKey: "mrqNumber",
    enableEditing: false,
    size: 120,
    muiTableHeadCellProps: {
      align: "center",
    },
    muiTableBodyCellProps: {
      align: "center",
    },
    Cell: ({ row }) => {
      const mrqNumbers = row.original.mrqMaterials
        ?.map((m) => m?.mrq?.mrqNumber)
        .filter(Boolean)
        .filter((v, i, arr) => arr.indexOf(v) === i); // unique
      return mrqNumbers && mrqNumbers.length > 0 ? (
        <span style={{ color: "green", fontWeight: "bold" }}>
          {mrqNumbers.join(", ")}
        </span>
      ) : (
        <span style={{ color: "gray" }}>-</span>
      );
    },
  },
  {
    header: "PO",
    accessorKey: "poNumber",
    enableEditing: false,
    size: 120,
    muiTableHeadCellProps: {
      align: "center",
    },
    muiTableBodyCellProps: {
      align: "center",
    },
    Cell: ({ row }) => {
      const poNumbers = row.original.mrqMaterials
        ?.flatMap((m) => 
          m?.poMaterials?.map((pm) => pm?.po?.poNumber).filter(Boolean) || []
        )
        .filter(Boolean)
        .filter((v, i, arr) => arr.indexOf(v) === i); // unique
      return poNumbers && poNumbers.length > 0 ? (
        <span style={{ color: "green", fontWeight: "bold" }}>
          {poNumbers.join(", ")}
        </span>
      ) : (
        <span style={{ color: "gray" }}>-</span>
      );
    },
  },
  {
    header: "MRV",
    accessorKey: "mrvNumber",
    enableEditing: false,
    size: 120,
    muiTableHeadCellProps: {
      align: "center",
    },
    muiTableBodyCellProps: {
      align: "center",
    },
    Cell: ({ row }) => {
      const mrvNumbers = [
        ...(row.original.mrvMaterials?.map((m) => m?.mrvNumber).filter(Boolean) || []),
        ...(row.original.mrqMaterials?.flatMap((m) => 
          m?.mrvMrqMaterials?.map((mrm) => mrm?.mrv?.mrvNumber).filter(Boolean) || []
        ) || []),
      ].filter((v, i, arr) => arr.indexOf(v) === i); // unique
      return mrvNumbers && mrvNumbers.length > 0 ? (
        <span style={{ color: "green", fontWeight: "bold" }}>
          {mrvNumbers.join(", ")}
        </span>
      ) : (
        <span style={{ color: "gray" }}>-</span>
      );
    },
  },
  buildSiColumn({ onSiChange, siUpdatingIds }),
  {
    accessorKey: "budgetedCost",
    header: "Budgeted Cost ($)",
    enableEditing: true,
    size: 130,
    muiTableHeadCellProps: {
      align: "center",
    },
    muiTableBodyCellProps: {
      align: "center",
    },
    Cell: ({ cell, row }) => {
      const value =
        row.original.budgetedCost ??
        row.original.estimatedTime ??
        cell.getValue();
      if (value === null || value === undefined || value === "") {
        return <span style={{ color: "gray" }}>-</span>;
      }
      const n = Number(value);
      if (!Number.isFinite(n)) {
        return <span style={{ color: "gray" }}>-</span>;
      }
      return `$${n.toFixed(2)}`;
    },
    muiEditTextFieldProps: {
      type: "number",
      inputProps: {
        step: 0.01,
        min: 0,
      },
    },
  },
  {
    accessorKey: "cost",
    header: "Cost ($)",
    enableEditing: false,
    size: 120,
    muiTableHeadCellProps: {
      align: "center",
    },
    muiTableBodyCellProps: {
      align: "center",
    },
    Cell: ({ row }) => {
      // Total cost = unit cost × quantity (summed across all POs)
      const totalCost = getTotalCost(row.original);
      const hasPo = row.original.mrqMaterials?.some(
        (m) => m?.poId || m?.poMaterials?.[0]?.po?.id
      );
      
      // Only show cost if PO exists
      if (!hasPo) {
        return <span style={{ color: "gray" }}>-</span>;
      }
      
      if (totalCost === null || totalCost === undefined) {
        return <span style={{ color: "gray" }}>-</span>;
      }
      
      const num = toNumber(totalCost);
      return Number.isFinite(num) ? `$${num.toFixed(2)}` : "-";
    },
  },
];

/** @deprecated Use buildSchedule4DColumns for SI toggle support */
export const schedule4DColumns = buildSchedule4DColumns();

